import type { User } from "firebase/auth";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
  type Unsubscribe
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes, uploadBytesResumable } from "firebase/storage";
import type { TrainingArticle, TrainingArticleStatus, TrainingCategory } from "../types/imobi";
import { db, storage } from "./firebase";

export type SaveTrainingCategoryInput = {
  name: string;
};

export type SaveTrainingArticleInput = {
  categoryId: string;
  title: string;
  summary?: string;
  bodyHtml: string;
  helpViewIds: string[];
  status: TrainingArticleStatus;
};

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function trainingImageFigureHtml(url: string): string {
  return `<figure data-media="image"><img src="${escapeHtml(url)}" alt=""></figure>`;
}

export function trainingVideoFigureHtml(url: string): string {
  return `<figure data-media="video"><video src="${escapeHtml(url)}" controls preload="metadata" playsinline></video></figure>`;
}

/**
 * Articles saved before inline media existed only have plain text plus separate
 * image/video lists. Rebuild them in the order the old viewer showed them:
 * video, images, then the text paragraphs.
 */
function buildLegacyTrainingHtml(body: string, imageUrls: string[], videoUrl?: string): string {
  const parts: string[] = [];
  if (videoUrl) {
    parts.push(trainingVideoFigureHtml(videoUrl));
  }
  imageUrls.forEach((url) => parts.push(trainingImageFigureHtml(url)));
  body
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
    .forEach((paragraph) => parts.push(`<p>${escapeHtml(paragraph).replace(/\n/g, "<br>")}</p>`));
  return parts.join("");
}

const ALLOWED_TRAINING_TAGS = new Set(["P", "DIV", "B", "STRONG", "U", "I", "EM", "SPAN", "UL", "OL", "LI", "BR", "H3", "FIGURE", "IMG", "VIDEO"]);

function isSafeMediaUrl(url: string | null): url is string {
  return Boolean(url && /^https:\/\//i.test(url));
}

/** Keeps only the tags/attributes the training editor produces. */
export function sanitizeTrainingHtml(html: string): string {
  if (typeof window === "undefined" || !html.trim()) {
    return html;
  }

  const parsed = new DOMParser().parseFromString(html, "text/html");

  function clean(node: ChildNode) {
    if (node.nodeType === Node.COMMENT_NODE) {
      node.remove();
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return;
    }
    const element = node as HTMLElement;
    if (!ALLOWED_TRAINING_TAGS.has(element.tagName)) {
      element.replaceWith(document.createTextNode(element.textContent ?? ""));
      return;
    }

    if (element.tagName === "IMG" || element.tagName === "VIDEO") {
      const src = element.getAttribute("src");
      if (!isSafeMediaUrl(src)) {
        element.remove();
        return;
      }
      Array.from(element.attributes).forEach((attribute) => element.removeAttribute(attribute.name));
      element.setAttribute("src", src);
      if (element.tagName === "IMG") {
        element.setAttribute("alt", "");
      } else {
        element.setAttribute("controls", "");
        element.setAttribute("preload", "metadata");
        element.setAttribute("playsinline", "");
      }
      return;
    }

    if (element.tagName === "FIGURE") {
      const media = element.querySelector("img, video");
      if (!media) {
        element.remove();
        return;
      }
      element.replaceChildren(media);
      Array.from(element.attributes).forEach((attribute) => element.removeAttribute(attribute.name));
      element.setAttribute("data-media", media.tagName === "IMG" ? "image" : "video");
      clean(media);
      if (!element.firstChild) {
        element.remove();
      }
      return;
    }

    const color = element.style?.color;
    const fontWeight = element.style?.fontWeight;
    const textDecoration = element.style?.textDecoration;
    Array.from(element.attributes).forEach((attribute) => element.removeAttribute(attribute.name));
    if (color) {
      element.style.color = color;
    }
    if (fontWeight) {
      element.style.fontWeight = fontWeight;
    }
    if (textDecoration && textDecoration.includes("underline")) {
      element.style.textDecoration = "underline";
    }

    Array.from(element.childNodes).forEach(clean);
  }

  Array.from(parsed.body.childNodes).forEach(clean);
  return parsed.body.innerHTML;
}

function trainingHtmlToPlainText(html: string): string {
  const parsed = new DOMParser().parseFromString(html, "text/html");
  const blocks: string[] = [];
  parsed.body.querySelectorAll("figure").forEach((figure) => figure.remove());
  parsed.body.querySelectorAll("br").forEach((br) => br.replaceWith("\n"));
  parsed.body.childNodes.forEach((node) => {
    const text = (node.textContent ?? "").trim();
    if (text) {
      blocks.push(text);
    }
  });
  return blocks.join("\n\n");
}

function extractTrainingMedia(html: string): { imageUrls: string[]; videoUrl?: string } {
  const parsed = new DOMParser().parseFromString(html, "text/html");
  const imageUrls = Array.from(parsed.body.querySelectorAll("img")).map((image) => image.getAttribute("src") ?? "").filter(Boolean);
  const videoUrl = parsed.body.querySelector("video")?.getAttribute("src") || undefined;
  return { imageUrls, videoUrl };
}

function buildTrainingArticleFields(input: SaveTrainingArticleInput) {
  const bodyHtml = sanitizeTrainingHtml(input.bodyHtml);
  const { imageUrls, videoUrl } = extractTrainingMedia(bodyHtml);
  return {
    categoryId: input.categoryId,
    title: input.title.trim(),
    summary: input.summary?.trim() || null,
    body: trainingHtmlToPlainText(bodyHtml),
    bodyHtml,
    imageUrls,
    videoUrl: videoUrl || null,
    helpViewIds: input.helpViewIds,
    status: input.status
  };
}

function mapTrainingCategory(id: string, data: Record<string, unknown>): TrainingCategory {
  return {
    id,
    name: String(data.name ?? ""),
    order: typeof data.order === "number" ? data.order : 0,
    active: data.active !== false,
    createdAt: typeof data.createdAt === "string" ? data.createdAt : "",
    createdBy: typeof data.createdBy === "string" ? data.createdBy : "",
    updatedAt: typeof data.updatedAt === "string" ? data.updatedAt : undefined,
    updatedBy: typeof data.updatedBy === "string" ? data.updatedBy : undefined
  };
}

function mapTrainingArticle(id: string, data: Record<string, unknown>): TrainingArticle {
  const body = String(data.body ?? "");
  const imageUrls = Array.isArray(data.imageUrls) ? data.imageUrls.map(String) : [];
  const videoUrl = data.videoUrl ? String(data.videoUrl) : undefined;
  return {
    id,
    categoryId: String(data.categoryId ?? ""),
    title: String(data.title ?? ""),
    summary: data.summary ? String(data.summary) : undefined,
    body,
    bodyHtml: typeof data.bodyHtml === "string" && data.bodyHtml.trim() ? data.bodyHtml : buildLegacyTrainingHtml(body, imageUrls, videoUrl),
    imageUrls,
    videoUrl,
    helpViewIds: Array.isArray(data.helpViewIds) ? data.helpViewIds.map(String) : [],
    order: typeof data.order === "number" ? data.order : 0,
    status: (data.status === "publicado" ? "publicado" : "rascunho") as TrainingArticleStatus,
    createdAt: typeof data.createdAt === "string" ? data.createdAt : "",
    createdBy: typeof data.createdBy === "string" ? data.createdBy : "",
    updatedAt: typeof data.updatedAt === "string" ? data.updatedAt : undefined,
    updatedBy: typeof data.updatedBy === "string" ? data.updatedBy : undefined
  };
}

export function subscribeTrainingCategories(onChange: (categories: TrainingCategory[]) => void, onError: (message: string) => void): Unsubscribe {
  if (!db) {
    onChange([]);
    return () => undefined;
  }

  return onSnapshot(
    query(collection(db, "trainingCategories"), orderBy("order")),
    (snapshot) => onChange(snapshot.docs.map((categoryDoc) => mapTrainingCategory(categoryDoc.id, categoryDoc.data()))),
    (error) => onError(error.message)
  );
}

export function subscribeTrainingArticles(onChange: (articles: TrainingArticle[]) => void, onError: (message: string) => void): Unsubscribe {
  if (!db) {
    onChange([]);
    return () => undefined;
  }

  return onSnapshot(
    query(collection(db, "trainingArticles"), orderBy("order")),
    (snapshot) => onChange(snapshot.docs.map((articleDoc) => mapTrainingArticle(articleDoc.id, articleDoc.data()))),
    (error) => onError(error.message)
  );
}

export async function createTrainingCategory(input: SaveTrainingCategoryInput, user: User): Promise<string> {
  if (!db) {
    throw new Error("Firestore não configurado.");
  }

  if (!input.name.trim()) {
    throw new Error("Informe o nome da categoria.");
  }

  const categoryRef = doc(collection(db, "trainingCategories"));
  await setDoc(categoryRef, {
    name: input.name.trim(),
    order: Date.now(),
    active: true,
    createdAt: serverTimestamp(),
    createdBy: user.uid
  });
  return categoryRef.id;
}

export async function updateTrainingCategory(categoryId: string, input: SaveTrainingCategoryInput, user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore não configurado.");
  }

  if (!input.name.trim()) {
    throw new Error("Informe o nome da categoria.");
  }

  await updateDoc(doc(db, "trainingCategories", categoryId), {
    name: input.name.trim(),
    updatedAt: serverTimestamp(),
    updatedBy: user.uid
  });
}

export async function reorderTrainingCategories(orderedIds: string[], user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore não configurado.");
  }

  const batch = writeBatch(db);
  orderedIds.forEach((categoryId, index) => {
    batch.update(doc(db!, "trainingCategories", categoryId), {
      order: index,
      updatedAt: serverTimestamp(),
      updatedBy: user.uid
    });
  });
  await batch.commit();
}

export async function setTrainingCategoryActive(categoryId: string, active: boolean, user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore não configurado.");
  }

  await updateDoc(doc(db, "trainingCategories", categoryId), {
    active,
    updatedAt: serverTimestamp(),
    updatedBy: user.uid
  });
}

export function generateTrainingArticleId(): string {
  if (!db) {
    return `local-${Date.now()}`;
  }

  return doc(collection(db, "trainingArticles")).id;
}

export async function createTrainingArticle(articleId: string, input: SaveTrainingArticleInput, user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore não configurado.");
  }

  if (!input.title.trim()) {
    throw new Error("Informe o título do treinamento.");
  }

  if (!input.categoryId) {
    throw new Error("Selecione uma categoria.");
  }

  await setDoc(doc(db, "trainingArticles", articleId), {
    ...buildTrainingArticleFields(input),
    order: Date.now(),
    createdAt: serverTimestamp(),
    createdBy: user.uid
  });
}

export async function updateTrainingArticle(articleId: string, input: SaveTrainingArticleInput, user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore não configurado.");
  }

  if (!input.title.trim()) {
    throw new Error("Informe o título do treinamento.");
  }

  if (!input.categoryId) {
    throw new Error("Selecione uma categoria.");
  }

  await updateDoc(doc(db, "trainingArticles", articleId), {
    ...buildTrainingArticleFields(input),
    updatedAt: serverTimestamp(),
    updatedBy: user.uid
  });
}

export async function reorderTrainingArticles(orderedIds: string[], user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore não configurado.");
  }

  const batch = writeBatch(db);
  orderedIds.forEach((articleId, index) => {
    batch.update(doc(db!, "trainingArticles", articleId), {
      order: index,
      updatedAt: serverTimestamp(),
      updatedBy: user.uid
    });
  });
  await batch.commit();
}

export async function uploadTrainingImage(articleId: string, file: File): Promise<string> {
  if (!storage) {
    throw new Error("Storage não configurado.");
  }

  const extension = file.name.split(".").pop()?.toLowerCase() || "jpg";
  const fileRef = ref(storage, `training/${articleId}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${extension}`);
  await uploadBytes(fileRef, file, { contentType: file.type || "image/jpeg" });
  return getDownloadURL(fileRef);
}

export function uploadTrainingVideo(
  articleId: string,
  file: File,
  onProgress: (percent: number) => void
): { promise: Promise<string>; cancel: () => void } {
  if (!storage) {
    return { promise: Promise.reject(new Error("Storage não configurado.")), cancel: () => undefined };
  }

  const extension = file.name.split(".").pop()?.toLowerCase() || "mp4";
  const fileRef = ref(storage, `training/${articleId}/video-${Date.now()}.${extension}`);
  const task = uploadBytesResumable(fileRef, file, { contentType: file.type || "video/mp4" });

  const promise = new Promise<string>((resolve, reject) => {
    task.on(
      "state_changed",
      (snapshot) => onProgress(snapshot.totalBytes ? Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100) : 0),
      (error) => reject(error),
      () => {
        getDownloadURL(task.snapshot.ref).then(resolve).catch(reject);
      }
    );
  });

  return { promise, cancel: () => task.cancel() };
}
